import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useStoreList } from "@/graphql/hooks/useStoreList"
import { useCountries, useRegions } from "@/graphql/hooks/Locations/useLocations"
import {
  Search,
  Edit,
  Store,
  Filter,
  Eye,
  Calendar,
  Mail,
  Phone,
  User,
  Users2,
  UserCheck,
  UserCircle,
} from "lucide-react"
import { StoreEditModal } from "./StoreEditModal"
import { CountryFlag } from "../ui/country-flag"
import { useNavigate } from "react-router-dom"

const LEVELS = [
  { id: 1, name: "Organizador" },
  { id: 2, name: "Tienda Oficial" },
  { id: 3, name: "Tienda Oficial Klu" },
]

export function StoresManagement() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [activeFilter, setActiveFilter] = useState<string>("")
  const [countryFilter, setCountryFilter] = useState<string>("")
  const [levelFilter, setLevelFilter] = useState<string>("")
  const [editingStore, setEditingStore] = useState(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)
    return () => clearTimeout(timer)
  }, [search])

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, activeFilter, countryFilter, levelFilter])

  const { countries, countriesMap } = useCountries()
  const { regionsMap } = useRegions()

  const filters = {
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    active: activeFilter === "active" ? 1 : activeFilter === "inactive" ? 2 : undefined,
    countryId: countryFilter ? parseInt(countryFilter) : undefined,
    levelId: levelFilter ? parseInt(levelFilter) : undefined,
  }

  const { stores, total, pages, loading, refetch } = useStoreList({ filter: filters })

  const clearFilters = () => {
    setSearch("")
    setDebouncedSearch("")
    setActiveFilter("")
    setCountryFilter("")
    setLevelFilter("")
    setPage(1)
  }

  const handleEdit = (store) => {
    setEditingStore(store)
  }

  const handleStoreUpdated = () => {
    refetch()
    setEditingStore(null)
  }

  const getLevelName = (levelId: number) => {
    return LEVELS.find((level) => level.id === levelId)?.name || "Sin nivel"
  }

  if (loading && page === 1) {
    return <LoadingScreen message="Cargando tiendas..." />
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros de búsqueda
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Active Status */}
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activas</SelectItem>
                <SelectItem value="inactive">Inactivas</SelectItem>
              </SelectContent>
            </Select>

            {/* Country */}
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los países</SelectItem>
                {countries?.map((country) => (
                  <SelectItem key={country.id} value={country.id.toString()}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Level */}
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Nivel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los niveles</SelectItem>
                {LEVELS.map((level) => (
                  <SelectItem key={level.id} value={level.id.toString()}>
                    {level.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(search || activeFilter || countryFilter || levelFilter) && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={clearFilters}>
                Limpiar Filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Tiendas ({total || 0})
            </CardTitle>
            <div className="text-sm text-muted-foreground">
              Página {page} de {pages || 1}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingScreen message="Cargando..." />
            </div>
          ) : stores.length === 0 ? (
            <div className="text-center py-8">
              <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No se encontraron tiendas</p>
            </div>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Logo</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Encargado / Email / Teléfono</TableHead>
                    <TableHead>Dirección</TableHead>
                    <TableHead>Nivel Tienda</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stores.map((store) => (
                    <TableRow key={store.id}>
                      <TableCell className="font-medium">{store.id}</TableCell>
                      <TableCell>
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted">
                          {store.urlLogo ? (
                            <img
                              src={store.urlLogo}
                              alt={store.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Store className="w-full h-full p-2 text-muted-foreground" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-40 truncate font-medium">{store.name}</div>
                        {store.description && (
                          <div className="text-sm text-muted-foreground max-w-40 truncate">
                            {store.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <p>
                          <UserCircle className="inline h-3" />
                          {store.inChargePerson}
                        </p>
                        <p className="truncate">
                          <Mail className="inline h-3" /> {store.email}
                        </p>
                        <p>
                          <Phone className="inline h-3" />
                          {store.phone}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p>
                          <CountryFlag
                            countryCode={countriesMap.get(store.Address?.countryId).iso}
                          />
                          {regionsMap.get(store.Address?.regionId)?.fullName || "N/A"}
                          {", "}
                          {store.Address?.Location?.name || ""}
                        </p>
                      </TableCell>
                      <TableCell className="flex items-center gap-2">
                        {store.Level?.icon && (
                          <img src={store.Level?.icon} className="w-6 inline" />
                        )}
                        <Badge variant="outline">{getLevelName(store.Level?.id || 1)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={store.active === 1 ? "default" : "destructive"}>
                          {store.active === 1 ? "Activa" : "Inactiva"}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-[160px] flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-gray-300 dark:border-gray-600"
                          onClick={() => navigate(store.urlView)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-gray-300 dark:border-gray-600"
                          onClick={() => navigate(store.urlTournaments)}
                        >
                          <Calendar className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(store)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    Anterior
                  </Button>
                  <div className="flex items-center px-3 text-sm">
                    {page} de {pages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === pages}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {editingStore && (
        <StoreEditModal
          store={editingStore}
          open={!!editingStore}
          onClose={() => setEditingStore(null)}
          onSuccess={handleStoreUpdated}
        />
      )}
    </div>
  )
}
